import { useCallback, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import type { CreateKBCardFooterMode } from './CreateKBCardFooter';
import type { SpaceOption } from './CreateKBCardPublishForm';

type CreateKbCardView = 'IMAGE_PREVIEW' | 'CREATE_SPACE_FORM' | 'PUBLISH_FORM';

type PublishConsentState = {
	isRequired: boolean;
	isChecked: boolean;
	isSpaceNameValid: boolean;
};

type PublishTargetState = {
	newSpaceName: string | null;
};

type UseCreateKbCardViewStateResult = {
	cardView: CreateKbCardView;
	spaceName: string;
	setSpaceName: Dispatch<SetStateAction<string>>;
	selectedSpace: SpaceOption | null;
	setSelectedSpace: Dispatch<SetStateAction<SpaceOption | null>>;
	publishNewSpaceName: string | null;
	isPublishConsentChecked: boolean;
	handlePublishConsentChange: (state: PublishConsentState) => void;
	handlePublishTargetChange: (state: PublishTargetState) => void;
	handleSaveForLater: () => void;
	handlePublishAll: () => void;
	handleBackToInitial: () => void;
	footerMode: CreateKBCardFooterMode;
	isConfirmDisabled: boolean;
	isPublishDisabled: boolean;
};

export const useCreateKbCardViewState = ({
	isArticlesCard,
	isConfirmLoading,
	projectIdentifier,
}: {
	isArticlesCard: boolean;
	isConfirmLoading: boolean;
	projectIdentifier: string | null;
}): UseCreateKbCardViewStateResult => {
	const [cardView, setCardView] = useState<CreateKbCardView>('IMAGE_PREVIEW');
	const [spaceName, setSpaceName] = useState('');
	const [selectedSpace, setSelectedSpace] = useState<SpaceOption | null>(null);
	const [publishNewSpaceName, setPublishNewSpaceName] = useState<string | null>(null);
	const [isPublishConsentRequired, setIsPublishConsentRequired] = useState(false);
	const [isPublishConsentChecked, setIsPublishConsentChecked] = useState(false);
	const [isPublishSpaceNameValid, setIsPublishSpaceNameValid] = useState(true);

	const resetPublishState = useCallback(() => {
		setIsPublishConsentRequired(false);
		setIsPublishConsentChecked(false);
		setIsPublishSpaceNameValid(true);
	}, []);

	const handleSaveForLater = useCallback(() => {
		setCardView('CREATE_SPACE_FORM');
	}, []);

	const handlePublishAll = useCallback(() => {
		resetPublishState();
		setCardView('PUBLISH_FORM');
	}, [resetPublishState]);

	const handleBackToInitial = useCallback(() => {
		resetPublishState();
		setCardView('IMAGE_PREVIEW');
	}, [resetPublishState]);

	const handlePublishConsentChange = useCallback(
		({ isRequired, isChecked, isSpaceNameValid }: PublishConsentState) => {
			setIsPublishConsentRequired(isRequired);
			setIsPublishConsentChecked(isChecked);
			setIsPublishSpaceNameValid(isSpaceNameValid);
		},
		[],
	);

	const handlePublishTargetChange = useCallback(({ newSpaceName }: PublishTargetState) => {
		setPublishNewSpaceName(newSpaceName);
	}, []);

	const footerMode = useMemo<CreateKBCardFooterMode>(
		() =>
			cardView === 'CREATE_SPACE_FORM'
				? 'FORM_ACTIONS'
				: cardView === 'PUBLISH_FORM'
					? 'PUBLISH_ACTIONS'
					: isArticlesCard
						? 'ARTICLE_ACTIONS'
						: 'CREATE_DRAFTS',
		[cardView, isArticlesCard],
	);

	const isConfirmDisabled = !spaceName.trim() || isConfirmLoading || !projectIdentifier;
	const isPublishDisabled =
		isConfirmLoading ||
		!projectIdentifier ||
		(!publishNewSpaceName && !selectedSpace) ||
		(isPublishConsentRequired && (!isPublishConsentChecked || !isPublishSpaceNameValid));

	return {
		cardView,
		spaceName,
		setSpaceName,
		selectedSpace,
		setSelectedSpace,
		publishNewSpaceName,
		isPublishConsentChecked,
		handlePublishConsentChange,
		handlePublishTargetChange,
		handleSaveForLater,
		handlePublishAll,
		handleBackToInitial,
		footerMode,
		isConfirmDisabled,
		isPublishDisabled,
	};
};
